// Centralized internal linking configuration for SEO cluster strategy
// Provides clustered related links for every page type

const MONEY_PAGES = [
  { path: '/equipment-finance', title: 'Equipment Finance', desc: 'Finance any business equipment with competitive rates from 40+ lenders.' },
  { path: '/truck-finance', title: 'Truck Finance', desc: 'Fast truck finance for prime movers, rigids, tippers, and trailers.' },
  { path: '/machinery-finance', title: 'Machinery Finance', desc: 'Finance heavy machinery and industrial equipment Australia-wide.' },
  { path: '/construction-equipment-finance', title: 'Construction Equipment Finance', desc: 'Excavators, loaders, cranes — financed fast for builders and contractors.' },
  { path: '/plant-and-equipment-finance', title: 'Plant & Equipment Finance', desc: 'Finance for all plant and equipment across every industry.' },
  { path: '/business-equipment-finance', title: 'Business Equipment Finance', desc: 'Finance the equipment your business needs to grow.' },
];

const PRODUCT_PAGES = [
  { path: '/chattel-mortgage', title: 'Chattel Mortgage', desc: 'Own assets from day one. Claim GST upfront. Most popular structure.' },
  { path: '/finance-lease', title: 'Finance Lease', desc: 'Lower repayments with flexible end-of-term options.' },
  { path: '/operating-lease', title: 'Operating Lease', desc: 'Use equipment without ownership. Upgrade at end of term.' },
  { path: '/hire-purchase', title: 'Hire Purchase', desc: 'Fixed payments with guaranteed ownership at end of term.' },
  { path: '/asset-refinance', title: 'Asset Refinance', desc: 'Release equity in assets you own. Lower payments or access cash.' },
  { path: '/low-doc-asset-finance', title: 'Low-Doc Finance', desc: 'Minimal paperwork. Fast approvals with ABN and bank statements.' },
];

const CALCULATOR_PAGES = [
  { path: '/equipment-finance-calculator', title: 'Equipment Finance Calculator', desc: 'Estimate monthly repayments for equipment purchases.' },
  { path: '/truck-finance-calculator', title: 'Truck Finance Calculator', desc: 'Calculate truck loan repayments instantly.' },
  { path: '/lease-vs-buy-calculator', title: 'Lease vs Buy Calculator', desc: 'Compare the true cost of leasing versus buying.' },
  { path: '/asset-refinance-calculator', title: 'Asset Refinance Calculator', desc: 'See how much you could save by refinancing.' },
];

const AUTHORITY_PAGES = [
  { path: '/equipment-finance-guide-australia', title: 'Complete Equipment Finance Guide', desc: 'Everything you need to know about equipment finance in Australia.' },
];

const LOCATION_CITIES = ['sydney', 'melbourne', 'brisbane', 'perth', 'adelaide', 'canberra', 'hobart', 'darwin'];
const STATE_GUIDE_PAGES = LOCATION_CITIES.map((city) => {
  const label = city.charAt(0).toUpperCase() + city.slice(1);
  return {
    path: `/locations/${city}`,
    title: `Asset Finance ${label}`,
    desc: `Truck, equipment and vehicle finance for ${label} businesses.`,
  };
});

const MORE_ASSET_PAGES = [
  { path: '/commercial-vehicle-finance', title: 'Commercial Vehicle Finance', desc: 'Finance for vans, utes, buses, and fleet vehicles.' },
  { path: '/agriculture-equipment-finance', title: 'Agriculture Equipment Finance', desc: 'Tractors, harvesters, and farm equipment with seasonal terms.' },
  { path: '/medical-equipment-finance', title: 'Medical Equipment Finance', desc: 'Finance for medical, dental, and healthcare equipment.' },
  { path: '/it-equipment-finance', title: 'IT Equipment Finance', desc: 'Servers, networking, and technology infrastructure finance.' },
];

const INDUSTRY_PAGES_LIST = [
  { path: '/finance-for-builders', title: 'Finance for Builders', desc: 'Equipment and vehicle finance for the building industry.' },
  { path: '/finance-for-electricians', title: 'Finance for Electricians', desc: 'Vehicle and tool finance for electrical contractors.' },
  { path: '/finance-for-plumbers', title: 'Finance for Plumbers', desc: 'Vehicle and equipment finance for plumbing businesses.' },
  { path: '/finance-for-tradies', title: 'Finance for Tradies', desc: 'Equipment and vehicle finance for Australian tradespeople.' },
  { path: '/finance-for-transport-companies', title: 'Finance for Transport', desc: 'Fleet and truck finance for transport operators.' },
  { path: '/finance-for-farmers', title: 'Finance for Farmers', desc: 'Agricultural equipment finance with seasonal repayments.' },
  { path: '/finance-for-construction-companies', title: 'Finance for Construction', desc: 'Heavy equipment finance for civil and commercial construction.' },
  { path: '/finance-for-dentists', title: 'Finance for Dentists', desc: 'Dental chair, imaging, and practice fit-out finance.' },
  { path: '/finance-for-medical-clinics', title: 'Finance for Medical Clinics', desc: 'Medical equipment and practice finance solutions.' },
  { path: '/finance-for-manufacturers', title: 'Finance for Manufacturers', desc: 'Machinery and production equipment finance.' },
];

const FINANCE_FINDER = { path: '/apply', title: 'Application Form', desc: 'Get matched with the best lenders instantly. No obligation.' };

/**
 * Get clustered related links for a given page, excluding itself
 * Returns { assetFinance: [], financeStructures: [], financeTools: [], industryGuides: [] }
 */
export function getClusterLinks(currentSlug, pageCategory) {
  const exclude = `/${currentSlug}`;

  const filterSelf = (arr) => arr.filter(p => p.path !== exclude);

  // Determine which calculators are most relevant based on page content
  const relevantCalcs = filterSelf(CALCULATOR_PAGES);
  const relevantAuthority = filterSelf(AUTHORITY_PAGES);

  if (pageCategory === 'asset') {
    // Asset finance pages get the most links: other asset pages + all products + all calculators + authority
    return {
      assetFinance: filterSelf([...MONEY_PAGES, ...MORE_ASSET_PAGES]).slice(0, 6),
      financeStructures: filterSelf(PRODUCT_PAGES).slice(0, 5),
      financeTools: [...relevantCalcs, ...relevantAuthority],
      stateGuides: filterSelf(STATE_GUIDE_PAGES).slice(0, 8),
      financeFinder: FINANCE_FINDER,
    };
  }

  if (pageCategory === 'product') {
    // Product pages link heavily to asset pages (money pages) + other products + calculators + authority
    return {
      assetFinance: filterSelf(MONEY_PAGES).slice(0, 5),
      financeStructures: filterSelf(PRODUCT_PAGES).slice(0, 4),
      financeTools: [...relevantCalcs, ...relevantAuthority],
      stateGuides: filterSelf(STATE_GUIDE_PAGES).slice(0, 6),
      financeFinder: FINANCE_FINDER,
    };
  }

  if (pageCategory === 'industry') {
    // Industry pages link to relevant asset pages + relevant products + relevant calculators
    const industryAssetMap = {
      'finance-for-builders': ['/construction-equipment-finance', '/equipment-finance', '/truck-finance', '/plant-and-equipment-finance'],
      'finance-for-electricians': ['/commercial-vehicle-finance', '/equipment-finance', '/business-equipment-finance'],
      'finance-for-plumbers': ['/commercial-vehicle-finance', '/equipment-finance', '/business-equipment-finance'],
      'finance-for-tradies': ['/equipment-finance', '/truck-finance', '/commercial-vehicle-finance', '/business-equipment-finance'],
      'finance-for-transport-companies': ['/truck-finance', '/commercial-vehicle-finance', '/equipment-finance'],
      'finance-for-farmers': ['/agriculture-equipment-finance', '/equipment-finance', '/machinery-finance', '/truck-finance'],
      'finance-for-construction-companies': ['/construction-equipment-finance', '/equipment-finance', '/truck-finance', '/plant-and-equipment-finance'],
      'finance-for-dentists': ['/medical-equipment-finance', '/equipment-finance', '/business-equipment-finance'],
      'finance-for-medical-clinics': ['/medical-equipment-finance', '/equipment-finance', '/business-equipment-finance', '/it-equipment-finance'],
      'finance-for-manufacturers': ['/machinery-finance', '/equipment-finance', '/plant-and-equipment-finance', '/business-equipment-finance'],
    };

    const relevantAssetPaths = industryAssetMap[currentSlug] || ['/equipment-finance', '/truck-finance', '/machinery-finance'];
    const allAssets = [...MONEY_PAGES, ...MORE_ASSET_PAGES];
    const assetLinks = relevantAssetPaths
      .map(p => allAssets.find(a => a.path === p))
      .filter(Boolean);

    return {
      assetFinance: assetLinks.slice(0, 4),
      financeStructures: filterSelf(PRODUCT_PAGES).slice(0, 3),
      financeTools: [...relevantCalcs.slice(0, 3), ...relevantAuthority.slice(0, 1)],
      stateGuides: filterSelf(STATE_GUIDE_PAGES).slice(0, 4),
      financeFinder: FINANCE_FINDER,
    };
  }

  if (pageCategory === 'location') {
    // Location pages link to main asset pages + products + calculators + authority
    return {
      assetFinance: filterSelf(MONEY_PAGES).slice(0, 4),
      financeStructures: filterSelf(PRODUCT_PAGES).slice(0, 3),
      financeTools: [...relevantCalcs.slice(0, 3), ...relevantAuthority.slice(0, 1)],
      stateGuides: filterSelf(STATE_GUIDE_PAGES).slice(0, 4),
      financeFinder: FINANCE_FINDER,
    };
  }

  // Default / other
  return {
    assetFinance: filterSelf(MONEY_PAGES).slice(0, 3),
    financeStructures: filterSelf(PRODUCT_PAGES).slice(0, 3),
    financeTools: [...relevantCalcs.slice(0, 2), ...relevantAuthority],
    stateGuides: filterSelf(STATE_GUIDE_PAGES).slice(0, 4),
    financeFinder: FINANCE_FINDER,
  };
}

/**
 * Get blog-specific cluster links based on article topic
 */
export function getBlogClusterLinks(slug, tags = []) {
  const tagSet = new Set(tags.map(t => t.toLowerCase()));

  // Determine relevant asset pages based on tags
  let assetLinks = [];
  if (tagSet.has('equipment') || tagSet.has('equipment finance')) {
    assetLinks.push(...MONEY_PAGES.filter(p => ['/equipment-finance', '/business-equipment-finance'].includes(p.path)));
  }
  if (tagSet.has('truck') || tagSet.has('truck finance')) {
    assetLinks.push(...MONEY_PAGES.filter(p => ['/truck-finance'].includes(p.path)));
  }
  if (tagSet.has('construction')) {
    assetLinks.push(...MONEY_PAGES.filter(p => ['/construction-equipment-finance', '/plant-and-equipment-finance'].includes(p.path)));
  }
  if (tagSet.has('machinery')) {
    assetLinks.push(...MONEY_PAGES.filter(p => ['/machinery-finance', '/plant-and-equipment-finance'].includes(p.path)));
  }
  // Always include at least 2 money pages
  if (assetLinks.length < 2) {
    assetLinks = MONEY_PAGES.slice(0, 3);
  }

  // Determine relevant calculators
  let calcLinks = [];
  if (tagSet.has('equipment') || tagSet.has('equipment finance')) calcLinks.push(CALCULATOR_PAGES[0]);
  if (tagSet.has('truck') || tagSet.has('truck finance')) calcLinks.push(CALCULATOR_PAGES[1]);
  if (tagSet.has('lease') || tagSet.has('comparison')) calcLinks.push(CALCULATOR_PAGES[2]);
  if (tagSet.has('refinance')) calcLinks.push(CALCULATOR_PAGES[3]);
  if (calcLinks.length === 0) calcLinks = CALCULATOR_PAGES.slice(0, 2);

  // Remove duplicates
  const seen = new Set();
  assetLinks = assetLinks.filter(l => { if (seen.has(l.path)) return false; seen.add(l.path); return true; });

  return {
    assetFinance: assetLinks.slice(0, 4),
    financeStructures: PRODUCT_PAGES.slice(0, 3),
    financeTools: [...calcLinks.slice(0, 3), ...AUTHORITY_PAGES.slice(0, 1)],
    stateGuides: STATE_GUIDE_PAGES.slice(0, 4),
    financeFinder: FINANCE_FINDER,
  };
}

export { MONEY_PAGES, PRODUCT_PAGES as PRODUCT_LINK_PAGES, CALCULATOR_PAGES, AUTHORITY_PAGES, MORE_ASSET_PAGES, INDUSTRY_PAGES_LIST, STATE_GUIDE_PAGES, FINANCE_FINDER };
